const monthValue = (line, monthIndex) => {
    const month = line?.monthsValue?.[monthIndex];
    const key = month && Object.keys(month)[0];
    return Number(key ? month[key] : 0) || 0;
};

const MONTH_SHORT_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const rollUpItems = (line) => {
    const value = line?.rollUpItems ?? line?.roll_up_items;
    if (Array.isArray(value)) return value.map(String);
    if (typeof value !== 'string') return [];
    try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch (error) {
        return [];
    }
};

export const getDisplayedBudgetLineValue = (line, monthIndex, actualsByMonth = [], lineItems = [], visiting = new Set()) => {
    const actuals = actualsByMonth[monthIndex];
    if (actuals?.lines) {
        const lineId = String(line?.id ?? '');
        if (lineId && visiting.has(lineId)) return 0;
        const children = rollUpItems(line);
        if (children.length) {
            const byId = new Map((lineItems || []).map((item) => [String(item?.id), item]));
            const nextVisiting = new Set(visiting);
            if (lineId) nextVisiting.add(lineId);
            return children.reduce((total, childId) => {
                const child = byId.get(childId);
                return total + (child
                    ? getDisplayedBudgetLineValue(child, monthIndex, actualsByMonth, lineItems, nextVisiting)
                    : 0);
            }, 0);
        }
        const accountNumber = String(line?.accountNumber ?? line?.account_number ?? '').trim();
        const accountName = String(line?.accountName ?? line?.account_name ?? '').trim();
        const key = accountNumber || `~${accountName}`;
        return Number(actuals.lines[key] || 0);
    }
    return monthValue(line, monthIndex);
};

export const getDisplayedBudgetLineTotal = (line, actualsByMonth = [], lineItems = []) => (
    Array.from({ length: 12 }, (_, index) => getDisplayedBudgetLineValue(line, index, actualsByMonth, lineItems))
        .reduce((total, value) => total + value, 0)
);

export const getDisplayedCategoryMonthValue = (
    forecastValue,
    items,
    monthIndex,
    actualsByMonth = [],
) => {
    if (!actualsByMonth[monthIndex]?.lines) return Number(forecastValue) || 0;
    const childIds = new Set((items || []).flatMap(rollUpItems));
    const topLevelItems = (items || []).filter((item) => !childIds.has(String(item?.id)));
    return topLevelItems.reduce(
        (total, item) => total + getDisplayedBudgetLineValue(item, monthIndex, actualsByMonth, items),
        0,
    );
};

export const getDisplayedNetIncomeMonthValue = (
    forecastValue,
    lineItems,
    categories,
    monthIndex,
    actualsByMonth = [],
) => {
    if (!actualsByMonth[monthIndex]?.lines) return Number(forecastValue) || 0;
    const categorySigns = new Map((categories || []).map((category) => [
        String(category.id),
        [1, 8].includes(Number(category.id)) ? 1 : -1,
    ]));
    const childIds = new Set((lineItems || []).flatMap(rollUpItems));
    const topLevelItems = (lineItems || []).filter((item) => !childIds.has(String(item?.id)));
    return topLevelItems.reduce((total, item) => (
        total + getDisplayedBudgetLineValue(item, monthIndex, actualsByMonth, lineItems)
            * (categorySigns.get(String(item.categoryId)) ?? -1)
    ), 0);
};

const formatBudgetMonth = (value) => {
    const match = String(value || '').slice(0, 7).match(/^(\d{4})-(\d{2})$/);
    if (!match) return String(value || 'Unknown month');
    const monthIndex = Number(match[2]) - 1;
    return `${MONTH_SHORT_NAMES[monthIndex] || match[2]} ${match[1]}`;
};

const nextCloseCheckDate = (now) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/Denver',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(now).reduce((values, part) => {
        if (part.type !== 'literal') values[part.type] = Number(part.value);
        return values;
    }, {});
    const monthIndex = parts.month - 1 + (parts.day >= 10 ? 1 : 0);
    const target = new Date(Date.UTC(parts.year, monthIndex, 10));
    return `${String(target.getUTCMonth() + 1).padStart(2, '0')}/10/${target.getUTCFullYear()}`;
};

export const getBudgetActualsPillState = (periods = [], now) => {
    const closedPeriods = (periods || [])
        .filter((period) => period.status === 'closed')
        .sort((left, right) => String(right.month).slice(0, 7).localeCompare(String(left.month).slice(0, 7)));
    const closedPeriodsWithActuals = closedPeriods.filter((period) => period.actuals?.lines);

    if (closedPeriodsWithActuals.length > 0) {
        const latestActualsPeriod = closedPeriodsWithActuals[0];
        return {
            label: `Actuals through ${formatBudgetMonth(latestActualsPeriod.month)}`,
            tone: 'actuals',
            title: `QuickBooks actuals are available through ${formatBudgetMonth(latestActualsPeriod.month)}.`,
        };
    }

    if (closedPeriods.length > 0) {
        return {
            label: 'Closed without actuals',
            tone: 'warning',
            title: 'Closed without a QuickBooks snapshot; showing locked forecast.',
        };
    }

    const blockedReasons = (periods || [])
        .filter((period) => period.actuals_lock_blocked?.reason)
        .map((period) => `${formatBudgetMonth(period.month)}: ${period.actuals_lock_blocked.reason}`);
    const nextCloseCheck = `Next close check: ${nextCloseCheckDate(now)}. On the 10th, Eadin flags any prior month Finance hasn't closed. Finance closes months in Budget Settings.`;
    const details = blockedReasons.length > 0 ? blockedReasons.join('\n') : nextCloseCheck;

    return {
        label: 'No months closed by Finance',
        tone: 'warning',
        title: `YTD Actuals shows QuickBooks only for months Finance has closed.\n\n${details}`,
    };
};
